package com.evoapps.evoshift.ui

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.domain.model.ThemeAccent
import com.evoapps.evoshift.domain.model.ThemeBase

/*
 * SH-196: the app's colours come from the base and the accent the person picked. Every colour
 * that was navy (the bars, the rail, the navy cards, dialogs and sheets) is the base's, every
 * gold one the accent's. Granat + Złoto is the app's own look, pixel for pixel.
 *
 * The other bases are derived from the navy family: each step keeps the navy's step's WCAG
 * luminance (so every pair of colours keeps the contrast it has on navy) with the base's own hue
 * and chroma. ThemeContrastTest checks every pair on all nine combinations, light (cream screens)
 * and dark (base-coloured dialogs, sheets and cards); the robots' contrast tests check the
 * rendered screens on all nine.
 */

/** The base colour's family: what is navy in the default look. */
@Immutable
data class BasePalette(
    /** The base itself (DeepNavy): bars, rail, dark cards and dialogs; text on cream. */
    val base: Color,
    /** The lighter end of the dark gradients (ReflectiveBlue). */
    val reflective: Color,
    /** The dark cards (CardBlue) and their gradient's highlight. */
    val card: Color,
    val cardHighlight: Color,
    /** A dialog's menus and tonal surfaces, a step lighter than the dialog, and a step more. */
    val container: Color,
    val containerHigh: Color,
    /** Outlines on the dark surfaces (non-text, 3:1). */
    val outline: Color,
    val outlineVariant: Color,
    /** Secondary text on the dark surfaces, and on the cream ones. */
    val onDarkVariant: Color,
    val onLightVariant: Color,
    /** Light tints of the base on the cream screens: a rest day, a note. */
    val restTint: Color,
    val noteTint: Color
)

/** The accent's tones: what is gold in the default look. */
@Immutable
data class AccentPalette(
    /** The vivid accent (Gold): Material's secondary, filled chips and badges with base text. */
    val vivid: Color,
    /** The metallic accent (DarkGold): text and icons on the dark surfaces, the metallic gradients. */
    val metallic: Color,
    /** The bright highlight (LightGold): text, icons and primary buttons on the dark surfaces. */
    val bright: Color,
    /** The deep accent: text, icons and outlines on the cream surfaces. */
    val deep: Color,
    /** A cream card with a hint of the accent (Material's surfaceVariant). */
    val tint: Color
)

@Immutable
data class ThemePalette(val appearance: Appearance, val base: BasePalette, val accent: AccentPalette)

object Palettes {
    val Navy = BasePalette(
        base = Color(0xFF002B49), reflective = Color(0xFF133A6B), card = Color(0xFF102E52), cardHighlight = Color(0xFF1C4578),
        container = Color(0xFF0B3A5E), containerHigh = Color(0xFF16466E), outline = Color(0xFF8193A6), outlineVariant = Color(0xFF3A5878),
        onDarkVariant = Color(0xFFC9D3DD), onLightVariant = Color(0xFF4A5B6B), restTint = Color(0xFFE4ECF6), noteTint = Color(0xFFEAF2FB)
    )
    val BottleGreen = BasePalette(
        base = Color(0xFF013023), reflective = Color(0xFF004339), card = Color(0xFF00352C), cardHighlight = Color(0xFF004F43),
        container = Color(0xFF094032), containerHigh = Color(0xFF134D3E), outline = Color(0xFF80968F), outlineVariant = Color(0xFF365D53),
        onDarkVariant = Color(0xFFC9D5D0), onLightVariant = Color(0xFF4B5D57), restTint = Color(0xFFE3EEEB), noteTint = Color(0xFFEAF4F0)
    )
    val Burgundy = BasePalette(
        base = Color(0xFF4F1322), reflective = Color(0xFF701921), card = Color(0xFF56171C), cardHighlight = Color(0xFF7E242B),
        container = Color(0xFF64202D), containerHigh = Color(0xFF752A37), outline = Color(0xFFAC898A), outlineVariant = Color(0xFF7F4549),
        onDarkVariant = Color(0xFFE1CECF), onLightVariant = Color(0xFF715155), restTint = Color(0xFFFAE7E7), noteTint = Color(0xFFFFEDEE)
    )
    val Gold = AccentPalette(
        vivid = Color(0xFFFFD700), metallic = Color(0xFFC5A065), bright = Color(0xFFF9E5B5), deep = Color(0xFF7A5A1E), tint = Color(0xFFFFFDEB)
    )
    val Bronze = AccentPalette(
        vivid = Color(0xFFEEA563), metallic = Color(0xFFDC9B64), bright = Color(0xFFFDDFC2), deep = Color(0xFF834B20), tint = Color(0xFFFFF8ED)
    )
    val Silver = AccentPalette(
        vivid = Color(0xFFD5D9DE), metallic = Color(0xFFC9CDD2), bright = Color(0xFFE8EBEE), deep = Color(0xFF5E6670), tint = Color(0xFFF8F9FA)
    )

    fun base(base: ThemeBase): BasePalette = when (base) {
        ThemeBase.NAVY -> Navy
        ThemeBase.BOTTLE_GREEN -> BottleGreen
        ThemeBase.BURGUNDY -> Burgundy
    }

    fun accent(accent: ThemeAccent): AccentPalette = when (accent) {
        ThemeAccent.GOLD -> Gold
        ThemeAccent.BRONZE -> Bronze
        ThemeAccent.SILVER -> Silver
    }

    private val all: Map<Appearance, ThemePalette> = ThemeBase.entries.flatMap { b ->
        ThemeAccent.entries.map { a -> Appearance(b, a) }
    }.associateWith { ThemePalette(it, base(it.base), accent(it.accent)) }

    fun of(appearance: Appearance): ThemePalette = all.getValue(appearance)

    /** Every combination, the default first. */
    val combinations: List<Appearance> = all.keys.toList()
}

/**
 * The appearance the app is drawn in now. Snapshot state: reading a colour while composing,
 * measuring or drawing follows a new pick. Set by ThemeSettings (the device's cache, at start)
 * and the Appearance picker; the brand colours that stay fixed (widgets, watch, store
 * screenshots, the web landing page) do not read it.
 */
object AppAppearance {
    var current: Appearance by mutableStateOf(Appearance.DEFAULT)

    val palette: ThemePalette get() = Palettes.of(current)
}

/** The fixed brand colours (Granat + Złoto), for what does not follow the person's pick. */
object BrandColors {
    val Navy = Palettes.Navy.base
    val ReflectiveBlue = Palettes.Navy.reflective
    val DarkGold = Palettes.Gold.metallic
    val LightGold = Palettes.Gold.bright
}

/**
 * A value built once for each of the nine combinations (a colour scheme, a brush); [current] is
 * the one of the appearance on show, read as snapshot state.
 */
class PerAppearance<T>(private val build: (ThemePalette) -> T) {
    private val values: Map<Appearance, T> by lazy { Palettes.combinations.associateWith { build(Palettes.of(it)) } }

    val current: T get() = values.getValue(AppAppearance.current)

    fun of(appearance: Appearance): T = values.getValue(appearance)
}
